import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { Box, Flex, Typography } from '@iheartjane/reefer';
import { Form, useFormContext } from '@iheartjane/reefer-hook-form';
import capitalize from 'lodash-es/capitalize';
import { useEffect, useMemo } from 'react';

import {
  VERIFICATION_TYPE_OPTIONS,
  getVerificationTypeLabel,
  useInitializeMedicalVerificationType,
} from '@jane/business-admin/components/shared/specials/modal/form';
import { useSpecialsModal } from '@jane/business-admin/providers/SpecialsModalProvider';
import type { SpecialTypeProps } from '@jane/business-admin/types';

import styles from './QualifiedGroup.module.css';

export const QualifiedGroup = ({
  isCreateMode,
  name,
  options,
}: SpecialTypeProps['qualified_group'] & {
  isCreateMode: boolean;
}) => {
  const {
    posSyncMap: { isJanePosSynced, posSynced },
    isReadOnly,
  } = useSpecialsModal();
  const { getValues, watch, setValue } = useFormContext();
  const qualifiedGroupValue = getValues('ruleSettings.qualified_group');
  const qualifiedGroupWatch = watch('ruleSettings.qualified_group');
  const specialTypeWatch = watch('special_type');

  const qualifiedGroupType = useMemo(
    () => qualifiedGroupWatch?.type || qualifiedGroupValue?.type,
    [qualifiedGroupWatch?.type, qualifiedGroupValue?.type]
  );

  const isSenior = qualifiedGroupType === 'senior';
  const medIdEnabled = useFlag(FLAGS.c3QualifiedGroupMedId);
  const isMedical = !!medIdEnabled && qualifiedGroupType === 'medical';

  const showInputs = (isJanePosSynced || !posSynced) && !isReadOnly;

  useEffect(() => {
    if (
      !qualifiedGroupType &&
      isCreateMode &&
      specialTypeWatch === 'qualified_group'
    ) {
      setValue('ruleSettings.qualified_group.type', 'senior');
    }
  }, []);

  useInitializeMedicalVerificationType({
    enabled: !!medIdEnabled,
    name,
    qualifiedGroupType,
  });

  return (
    <Flex justifyContent="space-between" width="100%" gap={24}>
      {isCreateMode || (!isCreateMode && showInputs) ? (
        <Flex width="100%" flexWrap="wrap" gap={24} mb={24}>
          <Form.SelectField
            width={isSenior || isMedical ? '100%' : '50%'}
            name={`${name}.type`}
            label="Qualified group"
            options={options || []}
          />
          {isMedical ? (
            <Box>
              <Typography variant="body-bold" mb={12}>
                Verification method
              </Typography>
              <Form.RadioFieldGroup
                name={`${name}.verification_type`}
                options={VERIFICATION_TYPE_OPTIONS}
                className={styles.verificationType}
              />
            </Box>
          ) : null}
          {isSenior ? (
            <Form.NumberField
              name={`${name}.required_age`}
              label="Age requirement"
              required={isCreateMode}
              width="100%"
              disableOnWheel
            />
          ) : null}
        </Flex>
      ) : (
        <>
          <Flex width="50%" flexDirection="column" mb={24}>
            <Typography color="grays-black" variant="body-bold">
              Qualified group
            </Typography>
            <Typography color="grays-black" variant="body" mt={4}>
              {capitalize(qualifiedGroupType)}
            </Typography>
          </Flex>
          {isMedical ? (
            <Flex width="50%" flexDirection="column">
              <Typography color="grays-black" variant="body-bold">
                Verification method
              </Typography>
              <Typography color="grays-black" variant="body" mt={4}>
                {getVerificationTypeLabel(
                  qualifiedGroupValue?.verification_type
                )}
              </Typography>
            </Flex>
          ) : null}
          {isSenior ? (
            <Flex width="50%" flexDirection="column">
              <Typography color="grays-black" variant="body-bold">
                Age requirement
              </Typography>
              <Typography color="grays-black" variant="body" mt={4}>
                {qualifiedGroupValue.required_age}
              </Typography>
            </Flex>
          ) : null}
        </>
      )}
    </Flex>
  );
};
